import React, { useCallback, useEffect, useState } from "react";
import { Layout, PageHeader } from "@/components/Layout";
import { Ban, Check, X, Clock, UserCheck, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtRM } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { useLang } from "@/context/LanguageContext";
import { ADMIN_ROLES } from "@/lib/badDebtReasons";

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const STAGE_LABEL = { not_assigned: "No Collector Action", chasing: "Collector Chasing" };

function StageBadge({ stage }) {
  const { tv } = useLang();
  const s = stage || "not_assigned";
  const cls = s === "chasing"
    ? "bg-blue-50 text-blue-700 border-blue-200"
    : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span data-testid={`stage-badge-${s}`} className={`inline-flex items-center gap-1 text-xs font-medium rounded-full border px-2 py-0.5 ${cls}`}>
      {s === "chasing" ? <UserCheck className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
      {tv(s)}
    </span>
  );
}

export default function BadDebt() {
  const { user } = useAuth();
  const { branch } = useBranch();
  const { t } = useLang();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const canEdit = user?.canEdit;
  const [data, setData] = useState({ loans: [], totalWrittenOff: 0, count: 0, stageTotals: {} });
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("all");
  const [assignFor, setAssignFor] = useState(null); // loan code being assigned
  const [collectorInput, setCollectorInput] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/bad-debt").then((r) => setData(r.data)),
      api.get("/bad-debt/requests", { params: { status: "pending" } }).then((r) => setRequests(r.data)).catch(() => setRequests([])),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, branch]);

  const review = async (id, action) => {
    try {
      await api.post(`/bad-debt/requests/${id}/${action}`);
      toast.success(action === "approve" ? "Loan marked as Bad Debt." : "Request rejected.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const setStage = async (code, stage, collectorName = "") => {
    try {
      await api.post(`/bad-debt/${code}/collector`, { stage, collectorName });
      toast.success(stage === "chasing" ? "Assigned to collector." : "Moved to 'No Collector Action'.");
      setAssignFor(null);
      setCollectorInput("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const st = data.stageTotals || {};
  const naT = st.not_assigned || { count: 0, outstanding: 0 };
  const chT = st.chasing || { count: 0, outstanding: 0 };
  const visibleLoans = (data.loans || []).filter(
    (l) => stageFilter === "all" || (l.collectorStage || "not_assigned") === stageFilter
  );

  return (
    <Layout>
      <PageHeader title={t("bd.title")} subtitle={t("bd.subtitle")} />

      <div className="mb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{t("bd.total_written")}</div>
          <div data-testid="total-written-off" className="font-mono-fig text-2xl font-bold mt-1 text-red-600">{fmtRM(data.totalWrittenOff)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{t("bd.loans")}</div>
          <div className="font-mono-fig text-2xl font-bold mt-1 text-slate-900">{data.count}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{t("bd.pending")}</div>
          <div className="font-mono-fig text-2xl font-bold mt-1 text-amber-600">{requests.length}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="card-not-assigned">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1"><Ban className="h-3 w-3" /> {t("bd.no_action")}</div>
          <div className="font-mono-fig text-2xl font-bold mt-1 text-slate-900">{naT.count}</div>
          <div className="text-xs text-slate-400 font-mono-fig">{fmtRM(naT.outstanding)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="card-chasing">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1"><UserCheck className="h-3 w-3" /> {t("bd.chasing")}</div>
          <div className="font-mono-fig text-2xl font-bold mt-1 text-blue-700">{chT.count}</div>
          <div className="text-xs text-slate-400 font-mono-fig">{fmtRM(chT.outstanding)}</div>
        </div>
      </div>

      {/* Pending requests */}
      {canEdit && requests.length > 0 && (
        <div className="mb-8" data-testid="pending-requests">
          <h3 className="font-heading font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-500" /> {t("bd.pending")}
          </h3>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                  <th className="px-4 py-3 font-semibold">{t("bd.col_loan")}</th>
                  <th className="px-4 py-3 font-semibold">{t("bd.col_customer")}</th>
                  <th className="px-4 py-3 font-semibold">{t("bd.col_branch")}</th>
                  <th className="px-4 py-3 font-semibold text-right">{t("bd.col_outstanding")}</th>
                  <th className="px-4 py-3 font-semibold">{t("bd.col_reason")}</th>
                  <th className="px-4 py-3 font-semibold">{t("bd.col_requested")}</th>
                  {isAdmin && canEdit && <th className="px-4 py-3 font-semibold text-right">{t("bd.col_action")}</th>}
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} data-testid="request-row" className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3"><Link to={`/loans/${r.loanCode}`} className="font-mono-fig text-slate-900 hover:underline">{r.loanCode}</Link></td>
                    <td className="px-4 py-3 text-slate-700">{r.customerName}</td>
                    <td className="px-4 py-3 text-slate-600">{r.branch}</td>
                    <td className="px-4 py-3 text-right font-mono-fig text-amber-600">{fmtRM(r.outstanding)}</td>
                    <td className="px-4 py-3 text-slate-700">{r.reason}{r.note ? <div className="text-xs text-slate-400">{r.note}</div> : null}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{r.requestedBy}<div>{fmtDate(r.requestedAt)}</div></td>
                    {isAdmin && canEdit && (
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-end">
                          <button data-testid={`approve-${r.loanCode}`} onClick={() => review(r.id, "approve")} className="bg-red-600 text-white rounded-md px-2.5 py-1.5 text-xs font-medium hover:bg-red-700 flex items-center gap-1"><Check className="h-3 w-3" /> {t("bd.approve")}</button>
                          <button data-testid={`reject-${r.loanCode}`} onClick={() => review(r.id, "reject")} className="bg-white border border-slate-300 rounded-md px-2.5 py-1.5 text-xs font-medium hover:bg-slate-50 flex items-center gap-1"><X className="h-3 w-3" /> {t("bd.reject")}</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bad debt loans */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
          <Ban className="h-4 w-4 text-red-500" /> {t("bd.loans")}
        </h3>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="stage-filter">
          {[["all", t("bd.f_all")], ["not_assigned", t("bd.f_none")], ["chasing", t("bd.f_chasing")]].map(([v, lbl]) => (
            <button
              key={v}
              data-testid={`stage-filter-${v}`}
              onClick={() => setStageFilter(v)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${stageFilter === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="bad-debt-table">
        {loading ? (
          <div className="p-8 text-slate-400 animate-pulse text-sm">{t("common.loading")}</div>
        ) : visibleLoans.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
            <Ban className="h-8 w-8 text-slate-300" /> {t("bd.none")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="px-4 py-3 font-semibold">{t("bd.col_loan")}</th>
                <th className="px-4 py-3 font-semibold">{t("bd.col_customer")}</th>
                <th className="px-4 py-3 font-semibold">{t("bd.col_branch")}</th>
                <th className="px-4 py-3 font-semibold text-right">{t("bd.col_outstanding")}</th>
                <th className="px-4 py-3 font-semibold">{t("bd.col_reason")}</th>
                <th className="px-4 py-3 font-semibold">{t("bd.col_stage")}</th>
                {canEdit && <th className="px-4 py-3 font-semibold text-right">{t("bd.col_action")}</th>}
              </tr>
            </thead>
            <tbody>
              {visibleLoans.map((l) => (
                <tr key={l.code} data-testid="bad-debt-row" className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3"><Link to={`/loans/${l.code}`} className="font-mono-fig text-slate-900 hover:underline">{l.code}</Link></td>
                  <td className="px-4 py-3 text-slate-700">{l.customerName}</td>
                  <td className="px-4 py-3 text-slate-600">{l.branch}</td>
                  <td className="px-4 py-3 text-right font-mono-fig text-red-600">{fmtRM(l.outstanding)}</td>
                  <td className="px-4 py-3 text-slate-700">{l.reason || "—"}{l.note ? <div className="text-xs text-slate-400">{l.note}</div> : null}</td>
                  <td className="px-4 py-3">
                    <StageBadge stage={l.collectorStage} />
                    {l.collectorStage === "chasing" && l.collectorName ? (
                      <div className="text-xs text-slate-500 mt-1">→ {l.collectorName}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    {canEdit && (
                    <div className="flex justify-end">
                      {(l.collectorStage || "not_assigned") === "chasing" ? (
                        <button
                          data-testid={`unassign-${l.code}`}
                          onClick={() => setStage(l.code, "not_assigned")}
                          className="bg-white border border-slate-300 rounded-md px-2.5 py-1.5 text-xs font-medium hover:bg-slate-50 flex items-center gap-1"
                        >
                          <X className="h-3 w-3" /> {t("bd.unassign")}
                        </button>
                      ) : (
                        <button
                          data-testid={`assign-collector-${l.code}`}
                          onClick={() => { setAssignFor(l.code); setCollectorInput(""); }}
                          className="bg-blue-600 text-white rounded-md px-2.5 py-1.5 text-xs font-medium hover:bg-blue-700 flex items-center gap-1"
                        >
                          <UserPlus className="h-3 w-3" /> {t("bd.assign")}
                        </button>
                      )}
                    </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Assign collector modal */}
      {assignFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="assign-modal" onClick={() => setAssignFor(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h4 className="font-heading font-semibold text-slate-900 mb-1">{t("bd.assign_title")}</h4>
            <p className="text-xs text-slate-500 mb-4">{t("bd.loan_word")} <span className="font-mono-fig">{assignFor}</span> — {t("bd.assign_hint")}</p>
            <input
              data-testid="collector-name-input"
              autoFocus
              value={collectorInput}
              onChange={(e) => setCollectorInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && collectorInput.trim()) setStage(assignFor, "chasing", collectorInput.trim()); }}
              placeholder={t("bd.collector_ph")}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-end gap-2">
              <button data-testid="assign-cancel" onClick={() => setAssignFor(null)} className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm font-medium hover:bg-slate-50">{t("bd.cancel")}</button>
              <button
                data-testid="assign-confirm"
                disabled={!collectorInput.trim()}
                onClick={() => setStage(assignFor, "chasing", collectorInput.trim())}
                className="bg-blue-600 text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1"
              >
                <Check className="h-3.5 w-3.5" /> {t("bd.assign_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
